import { defineStore } from "pinia";
import { ref, Ref } from "vue";
import { z } from "zod";

import { useLogger } from "@vm/logger";

import { ConfigResolutionService } from "./config-resolution-service";
import { ConfigSource } from "./sources/config-source";

export function createConfigStore<T extends Record<string, unknown>>(
  id: string,
  sources: ConfigSource<T>[],
  schema?: z.ZodType<T>,
) {
  return defineStore(id, () => {
    const configValues = ref({} as T) as Ref<T>;
    const isInitialized = ref(false);
    const error = ref<Error | null>(null);
    const resolutionService = new ConfigResolutionService<T>(schema);
    const { logger } = useLogger();

    async function initialize(): Promise<null | Error> {
      try {
        if (sources.length === 0) {
          throw new Error("No configuration sources provided");
        }

        sources.forEach((source) => resolutionService.addSource(source));
        await resolutionService.initializeConfigSources();
        await resetConfigValues();

        if (schema) {
          schema.parse(configValues.value);
        }

        setupChangeListeners(resolutionService, configValues);

        isInitialized.value = true;
        return null;
      } catch (err) {
        error.value = err instanceof Error ? err : new Error(String(err));
        logger.error("Initialization failed", error.value);
        return error.value;
      }
    }

    async function resetConfigValues() {
      const resolvedConfig = await resolutionService.resolveConfigValues();
      resolvedConfig.forEach((resolved, key) => {
        configValues.value[key as keyof T] = resolved.value;
      });
    }

    return {
      configValues,
      isInitialized,
      error,
      initialize,
      fetchAndStoreConfig: resetConfigValues,
    };
  });

  function setupChangeListeners(
    service: ConfigResolutionService<T>,
    values: Ref<T>,
  ) {
    service.setupChangeListeners((key, value) => {
      values.value[key as keyof T] = value;
    });
  }
}
